import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { TextareaContentEditable } from '@dndbeyond/character-common-components/es';
import {
    characterActions,
    Constants,
    RuleDataUtils,
    rulesEngineSelectors,
    CharacterNotes,
} from '@dndbeyond/character-rules-engine/es';

import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeader } from '../../../components/Sidebar';
import { ThemeButton } from '../../../components/common/Button';
import { SharedAppState } from '../../../stores/typings';
import { PaneIdentifiersNote } from '../../../utils/PaneIdentifier/typings';

interface Props extends DispatchProp {
    notes: CharacterNotes;
    placeholder: string;
    autoSaveDelay: number;
    identifiers: PaneIdentifiersNote | null;
}
interface StateData {
    noteType: string | null;
}
interface State extends StateData {
    hasBeenEdited: boolean;
    isSaveDirty: boolean;
}
class NoteManagePane extends React.PureComponent<Props, State> {
    static defaultProps = {
        placeholder: 'Enter any notes here!',
        autoSaveDelay: 5000,
    };

    autoSaveTimeoutId: number | null = null;
    textareaInput = React.createRef<TextareaContentEditable>();

    constructor(props) {
        super(props);

        this.state = {
            ...this.generateStateData(props),
            hasBeenEdited: false,
            isSaveDirty: false,
        };
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { identifiers } = this.props;

        if (identifiers !== prevProps.identifiers) {
            this.setState({
                ...this.generateStateData(this.props),
                hasBeenEdited: false,
                isSaveDirty: false,
            });
        }
    }

    generateStateData = (props: Props): StateData => {
        const { identifiers } = props;

        return {
            noteType: identifiers !== null ? identifiers.noteType : null,
        };
    };

    componentDidMount() {
        if (this.textareaInput.current) {
            this.textareaInput.current.focus();
        }
    }

    componentWillUnmount() {
        const { dispatch } = this.props;
        const { isSaveDirty, noteType } = this.state;

        this.handleStopAutoSaveTimer();

        if (noteType !== null && isSaveDirty && this.textareaInput.current) {
            dispatch(characterActions.noteSet(noteType, this.textareaInput.current.getTextValue()));
        }
    }

    startAutoSaveTimer = (): void => {
        const { autoSaveDelay } = this.props;

        if (this.autoSaveTimeoutId === null) {
            this.autoSaveTimeoutId = window.setTimeout(() => {
                this.handleSave();
            }, autoSaveDelay);
        }
    };

    handleStopAutoSaveTimer = (): void => {
        if (this.autoSaveTimeoutId !== null) {
            clearTimeout(this.autoSaveTimeoutId);
            this.autoSaveTimeoutId = null;
        }
    };

    handleSave = (): void => {
        const { dispatch } = this.props;
        const { noteType } = this.state;

        if (noteType !== null && this.textareaInput.current) {
            dispatch(characterActions.noteSet(noteType, this.textareaInput.current.getTextValue()));
        }

        this.handleStopAutoSaveTimer();
        this.setState({
            isSaveDirty: false,
        });
    };

    handleInputKeyUp = (): void => {
        const { isSaveDirty } = this.state;

        if (!isSaveDirty) {
            this.setState(
                {
                    isSaveDirty: true,
                    hasBeenEdited: true,
                },
                this.startAutoSaveTimer,
            );
        }
    };

    handleInputBlur = (text: string): void => {
        this.handleSave();
    };

    render() {
        const { isSaveDirty, hasBeenEdited, noteType } = this.state;
        const { notes, placeholder } = this.props;

        if (noteType === null) {
            return <PaneInitFailureContent />;
        }

        let content: string | null = notes[noteType] ? notes[noteType] : '';

        let calloutNode: React.ReactNode;
        if (hasBeenEdited) {
            if (isSaveDirty) {
                calloutNode = (
                    <React.Fragment>
                        <div className="ct-note-manage-pane__status ct-note-manage-pane__status--dirty">
                            <div className="ct-note-manage-pane__status-text">Unsaved changes</div>
                        </div>
                        <ThemeButton size="small" onClick={this.handleSave}>
                            Save
                        </ThemeButton>
                    </React.Fragment>
                );
            } else {
                calloutNode = (
                    <div className="ct-note-manage-pane__status ct-note-manage-pane__status--clean">
                        <div className="ct-note-manage-pane__status-icon" />
                        <div className="ct-note-manage-pane__status-text">Saved</div>
                    </div>
                );
            }
        }

        return (
            <div className="ct-note-manage-pane">
                <SidebarHeader callout={calloutNode}>
                    {RuleDataUtils.getNoteKeyName(noteType as Constants.NoteKeyEnum)}
                </SidebarHeader>
                <div className="ct-note-manage-pane__content">
                    <TextareaContentEditable
                        key={noteType}
                        ref={this.textareaInput}
                        className="ct-note-manage-pane__input"
                        placeholder={placeholder}
                        initialContent={content === null ? '' : content}
                        onInputKeyUp={this.handleInputKeyUp}
                        onInputBlur={this.handleInputBlur}
                    />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        notes: rulesEngineSelectors.getCharacterNotes(state),
    };
}

export default connect(mapStateToProps)(NoteManagePane);
